/**
 * Turning a version snapshot into something readable.
 *
 * The snapshot is the document's storage shape, not a display shape: every
 * block type keeps its own content object, so there is no single field to read.
 * This pulls out just enough to recognise a version — which is the whole job of
 * a preview. It is deliberately lossy and deliberately not a renderer; showing
 * the real thing is what restoring is for.
 *
 * Kept separate from the panel so the extraction can be tested without mounting
 * a dialog, and tolerant throughout: a preview must never be the reason a
 * history panel throws.
 */
export interface PreviewBlock {
  id: string;
  type: string;
  text: string;
}

export interface VersionSnapshotShape {
  title?: unknown;
  blocks?: unknown;
}

/** Fields that carry human-readable text, in the order worth trying. */
const TEXT_KEYS = ["text", "latex", "caption", "title", "code", "content", "url"];

function textOf(content: unknown): string {
  if (typeof content === "string") return content;
  if (!content || typeof content !== "object") return "";
  const rec = content as Record<string, unknown>;

  for (const key of TEXT_KEYS) {
    const v = rec[key];
    if (typeof v === "string" && v.trim()) return v.trim();
  }

  // Lists and tables keep their text in arrays.
  for (const v of Object.values(rec)) {
    if (Array.isArray(v)) {
      const joined = v
        .map((item) => (typeof item === "string" ? item : textOf(item)))
        .filter(Boolean)
        .join(" · ");
      if (joined.trim()) return joined.trim();
    }
  }
  return "";
}

export function readSnapshotBlocks(snapshot: VersionSnapshotShape | undefined): PreviewBlock[] {
  if (!snapshot || !Array.isArray(snapshot.blocks)) return [];
  const out: PreviewBlock[] = [];

  for (const raw of snapshot.blocks) {
    if (!raw || typeof raw !== "object") continue;
    const b = raw as Record<string, unknown>;
    out.push({
      id: typeof b.id === "string" ? b.id : `${out.length}`,
      type: typeof b.type === "string" ? b.type : "paragraph",
      text: textOf(b.content),
    });
  }
  return out;
}

export function snapshotTitle(snapshot: VersionSnapshotShape | undefined): string | null {
  return snapshot && typeof snapshot.title === "string" ? snapshot.title : null;
}
